<template>
    <Teleport to="body">
        <TransitionGroup
            name="app-toast"
            tag="div"
            class="app-toast-host"
            :class="hostClass"
            role="status"
            aria-live="polite"
        >
            <article
                v-for="item in items"
                :key="item.id"
                class="app-toast"
                :class="{ 'app-toast--link': !!item.onClick }"
                @click="activate(item)"
            >
                <div class="app-toast__body">
                    <p class="app-toast__text">{{ item.text }}</p>
                    <span class="app-toast__time"
                        >{{ secondsLeft(item) }}s</span
                    >
                    <button
                        type="button"
                        class="app-toast__close"
                        aria-label="Close"
                        @click.stop="dismiss(item.id)"
                    >
                        <svg
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"
                            />
                        </svg>
                    </button>
                </div>
                <div class="app-toast__track" aria-hidden="true">
                    <div
                        class="app-toast__bar"
                        :style="{ width: progress(item) + '%' }"
                    ></div>
                </div>
            </article>
        </TransitionGroup>
    </Teleport>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from "vue";
import { useTheme } from "vuetify";

const props = defineProps({
    location: {
        type: String,
        default: "top end",
        validator: (value) =>
            [
                "top start",
                "top",
                "top end",
                "bottom start",
                "bottom",
                "bottom end",
            ].includes(value),
    },
    timeout: {
        type: Number,
        default: 5000,
    },
});

const theme = useTheme();
const isDark = computed(() => theme.global.current.value.dark);

const hostClass = computed(() => {
    const [vertical, horizontal = "center"] = props.location.split(" ");
    return {
        "v-theme--dark": isDark.value,
        "app-toast-host--top": vertical !== "bottom",
        "app-toast-host--bottom": vertical === "bottom",
        "app-toast-host--start": horizontal === "start",
        "app-toast-host--end": horizontal === "end",
        "app-toast-host--center":
            horizontal !== "start" && horizontal !== "end",
    };
});

const items = ref([]);
const timers = new Map();
let seq = 0;

function resolveDuration(options) {
    const requested = Number(options?.timeout ?? props.timeout);
    if (Number.isFinite(requested) && requested > 0) return requested;
    return 5000;
}

function show(text, options = {}) {
    const duration = resolveDuration(options);
    const id = ++seq;
    items.value.unshift({
        id,
        text: text == null ? "" : String(text),
        duration,
        remaining: duration,
        onClick: typeof options.onClick === "function" ? options.onClick : null,
    });
    const timer = setInterval(() => {
        const current = items.value.find((entry) => entry.id === id);
        if (!current) {
            clearInterval(timer);
            timers.delete(id);
            return;
        }
        current.remaining = Math.max(0, current.remaining - 100);
        if (current.remaining <= 0) dismiss(id);
    }, 100);
    timers.set(id, timer);
    return id;
}

function activate(item) {
    if (typeof item.onClick !== "function") return;
    item.onClick();
    dismiss(item.id);
}

function dismiss(id) {
    const timer = timers.get(id);
    if (timer) {
        clearInterval(timer);
        timers.delete(id);
    }
    const index = items.value.findIndex((entry) => entry.id === id);
    if (index !== -1) items.value.splice(index, 1);
}

function secondsLeft(item) {
    return Math.max(1, Math.ceil(item.remaining / 1000));
}

function progress(item) {
    if (!item.duration) return 0;
    return Math.max(0, Math.min(100, (item.remaining / item.duration) * 100));
}

onBeforeUnmount(() => {
    timers.forEach((timer) => clearInterval(timer));
    timers.clear();
});

defineExpose({ show, dismiss });
</script>

<style lang="scss" scoped>
.app-toast-host {
    position: fixed;
    z-index: 11000;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: min(360px, calc(100vw - 32px));
    pointer-events: none;
}

.app-toast-host--top {
    top: 66px;
}

.app-toast-host--bottom {
    bottom: 16px;
    flex-direction: column-reverse;
}

.app-toast-host--start {
    left: 16px;
    align-items: flex-start;
}

.app-toast-host--end {
    right: 16px;
    align-items: flex-end;
}

.app-toast-host--center {
    left: 50%;
    transform: translateX(-50%);
    align-items: center;
}

.app-toast {
    pointer-events: auto;
    width: 100%;
    overflow: hidden;
    background: var(--card);
    color: var(--foreground);
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1),
        0 4px 6px -4px rgb(0 0 0 / 0.1);
    transition: border-color 150ms ease;
}

.app-toast--link {
    cursor: pointer;

    &:hover {
        border-color: var(--ring);
    }
}

.v-theme--dark.app-toast-host .app-toast {
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.45),
        0 4px 6px -4px rgb(0 0 0 / 0.4);
}

.app-toast__body {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
}

.app-toast__text {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.25rem;
}

.app-toast__time {
    flex-shrink: 0;
    font-size: 12px;
    line-height: 1.25rem;
    color: var(--muted-foreground);
}

.app-toast__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    margin-top: 1px;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: var(--muted-foreground);
    cursor: pointer;
    transition: color 150ms ease, background-color 150ms ease;

    svg {
        width: 16px;
        height: 16px;
    }

    &:hover {
        color: var(--foreground);
        background: var(--muted);
    }

    &:focus-visible {
        outline: 2px solid var(--ring);
        outline-offset: 2px;
    }
}

.app-toast__track {
    height: 2px;
    background: var(--muted);
}

.app-toast__bar {
    height: 100%;
    background: var(--primary);
    transition: width 100ms linear;
}

.app-toast-enter-active,
.app-toast-leave-active,
.app-toast-move {
    transition: opacity 200ms ease, transform 200ms ease;
}

.app-toast-host--top .app-toast-enter-from,
.app-toast-host--top .app-toast-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

.app-toast-host--bottom .app-toast-enter-from,
.app-toast-host--bottom .app-toast-leave-to {
    opacity: 0;
    transform: translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
    .app-toast-enter-active,
    .app-toast-leave-active,
    .app-toast-move,
    .app-toast,
    .app-toast__bar,
    .app-toast__close {
        transition: none;
    }
}
</style>
